import React from 'react';
import {
  Palette,
  Camera,
  Video,
  Music,
  Brush,
  Film,
  Users,
  BookOpen,
  Award,
  TrendingUp,
  Lightbulb,
  Settings,
  Shield,
  Star,
  Briefcase,
  Globe,
  Mic,
  Edit,
  Play,
  Image
} from 'lucide-react';
import '../Css_files/arts_multi.css';
import Navbar from './Navbar';
import Footer from './Footer';

const ArtsAndMultimedia = () => {
  const programs = [
    {
      title: 'B.A. Digital Arts & Design',
      duration: '4 Years',
      description: 'Comprehensive undergraduate program covering all aspects of digital arts and multimedia design',
      features: ['Graphic Design Fundamentals', 'Digital Photography', 'Video Production', 'Portfolio Development']
    },
    {
      title: 'M.A. Animation & VFX',
      duration: '2 Years',
      description: 'Advanced study of 2D/3D animation, motion graphics, and visual effects for film and games',
      features: ['3D Modeling & Animation', 'Visual Effects', 'Motion Graphics', 'Industry Projects']
    },
    {
      title: 'M.A. Interactive Media',
      duration: '2 Years',
      description: 'Specialized program focusing on UI/UX design, web design, and interactive experiences',
      features: ['UI/UX Design', 'Web Development', 'Interactive Design', 'User Research']
    }
  ];

  const faculty = [
    {
      name: 'Prof. Maria Rodriguez',
      position: 'Head of Department',
      specialization: 'Digital Arts & Interactive Media',
      experience: '18+ years',
      image: 'https://images.pexels.com/photos/3182821/pexels-photo-3182821.jpeg?auto=compress&cs=tinysrgb&w=300'
    },
    {
      name: 'Dr. James Wilson',
      position: 'Professor',
      specialization: 'Animation & Visual Effects',
      experience: '15+ years',
      image: 'https://images.pexels.com/photos/3182812/pexels-photo-3182812.jpeg?auto=compress&cs=tinysrgb&w=300'
    },
    {
      name: 'Prof. Sarah Chen',
      position: 'Associate Professor',
      specialization: 'Photography & Video Production',
      experience: '12+ years',
      image: 'https://images.pexels.com/photos/3182824/pexels-photo-3182824.jpeg?auto=compress&cs=tinysrgb&w=300'
    }
  ];

  const specializations = [
    { name: 'Graphic Design', icon: Brush, description: 'Visual communication, branding, and digital design principles.' },
    { name: 'Digital Photography', icon: Camera, description: 'Professional photography, photo editing, and visual storytelling.' },
    { name: 'Video Production', icon: Video, description: 'Film making, video editing, and multimedia content creation.' },
    { name: 'Audio Engineering', icon: Music, description: 'Sound design, music production, and audio post-production.' },
    { name: 'Animation & VFX', icon: Film, description: '2D/3D animation, motion graphics, and visual effects.' },
    { name: 'Web Design', icon: Edit, description: 'UI/UX design, interactive media, and user experience.' }
  ];

  const facilities = [
    {
      title: 'Digital Arts Studio',
      description: 'Professional workstations with latest creative software and high-end displays',
      icon: <Palette className="arts-multi-facility-icon" />
    },
    {
      title: 'Photography Studio',
      description: 'Professional lighting equipment and backdrop systems for studio photography',
      icon: <Camera className="arts-multi-facility-icon" />
    },
    {
      title: 'Video Production Lab',
      description: 'Professional cameras, editing suites, and post-production facilities',
      icon: <Video className="arts-multi-facility-icon" />
    },
    {
      title: 'Audio Recording Studio',
      description: 'Soundproof recording booths and professional audio mixing equipment',
      icon: <Music className="arts-multi-facility-icon" />
    }
  ];

  const careerStats = [
    { icon: <Users />, value: '96%', label: 'Placement Rate' },
    { icon: <Award />, value: '₹8 LPA', label: 'Average Package' },
    { icon: <Briefcase />, value: '150+', label: 'Industry Partners' },
    { icon: <Star />, value: '4.8/5', label: 'Student Rating' }
  ];

  const testimonials = [
    {
      name: 'Maya Patel',
      role: 'Creative Director, Ogilvy',
      year: '2021 Graduate',
      content: 'The arts and multimedia program gave me the creative foundation and technical skills needed to excel in the advertising industry.',
      image: 'https://images.pexels.com/photos/3182824/pexels-photo-3182824.jpeg?auto=compress&cs=tinysrgb&w=150'
    },
    {
      name: 'David Kim',
      role: 'VFX Artist, Disney Studios',
      year: '2020 Graduate',
      content: 'The animation and VFX specialization prepared me perfectly for working on major film productions and bringing stories to life.',
      image: 'https://images.pexels.com/photos/3182773/pexels-photo-3182773.jpeg?auto=compress&cs=tinysrgb&w=150'
    }
  ];

  return (
    <>
    <Navbar/><br></br><br></br>
    <div className="arts-multi-page">
      {/* Hero Section */}
      <section className="arts-multi-hero">
        <div className="arts-multi-hero-background">
          <div className="arts-multi-animated-brushes">
            <div className="arts-multi-brush arts-multi-brush-1"></div>
            <div className="arts-multi-brush arts-multi-brush-2"></div>
            <div className="arts-multi-brush arts-multi-brush-3"></div>
            <div className="arts-multi-brush arts-multi-brush-4"></div>
          </div>
          <div className="arts-multi-floating-colors">
            <div className="arts-multi-color arts-multi-color-1"></div>
            <div className="arts-multi-color arts-multi-color-2"></div>
            <div className="arts-multi-color arts-multi-color-3"></div>
            <div className="arts-multi-color arts-multi-color-4"></div>
            <div className="arts-multi-color arts-multi-color-5"></div>
          </div>
        </div>
        <div className="arts-multi-hero-overlay"></div>
        <div className="arts-multi-hero-content">
          <div className="arts-multi-hero-logo">
            <div className="arts-multi-logo-palette">
              <Palette className="arts-multi-logo-icon" />
            </div>
          </div>
          <h1 className="arts-multi-hero-title">
            Arts & Multimedia
            <span className="arts-multi-hero-subtitle">Creating Visual Stories That Inspire</span>
          </h1>
          <p className="arts-multi-hero-description">
            Unleash your creativity and master the digital arts that shape modern communication and entertainment. 
            From graphic design to animation, photography to video production - create the visual experiences of tomorrow.
          </p>
          <div className="arts-multi-hero-actions">
            <button className="arts-multi-btn arts-multi-btn-primary">Apply Now</button>
            <button className="arts-multi-btn arts-multi-btn-secondary">Download Brochure</button>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="arts-multi-stats">
        <div className="arts-multi-container">
          <div className="arts-multi-stats-grid">
            {careerStats.map((stat, index) => (
              <div key={index} className="arts-multi-stat-card">
                <div className="arts-multi-stat-icon">{stat.icon}</div>
                <div className="arts-multi-stat-value">{stat.value}</div>
                <div className="arts-multi-stat-label">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Programs Section */}
      <section className="arts-multi-programs">
        <div className="arts-multi-container">
          <div className="arts-multi-section-header">
            <h2 className="arts-multi-section-title">Academic Programs</h2>
            <p className="arts-multi-section-subtitle">
              Comprehensive programs designed to nurture creativity and develop technical expertise in digital arts
            </p>
          </div>
          <div className="arts-multi-programs-grid">
            {programs.map((program, index) => (
              <div key={index} className="arts-multi-program-card">
                <div className="arts-multi-program-header">
                  <h3 className="arts-multi-program-title">{program.title}</h3>
                  <span className="arts-multi-program-duration">{program.duration}</span>
                </div>
                <p className="arts-multi-program-description">{program.description}</p>
                <ul className="arts-multi-program-features">
                  {program.features.map((feature, idx) => (
                    <li key={idx} className="arts-multi-program-feature">
                      <span className="arts-multi-feature-bullet"></span>
                      {feature}
                    </li>
                  ))}
                </ul>
                <button className="arts-multi-program-btn">Learn More</button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Faculty Section */}
      <section className="arts-multi-faculty">
        <div className="arts-multi-container">
          <div className="arts-multi-section-header">
            <h2 className="arts-multi-section-title">Creative Faculty</h2>
            <p className="arts-multi-section-subtitle">
              Learn from industry professionals and renowned artists in multimedia and digital arts
            </p>
          </div>
          <div className="arts-multi-faculty-grid">
            {faculty.map((member, index) => (
              <div key={index} className="arts-multi-faculty-card">
                <div className="arts-multi-faculty-image">
                  <img src={member.image} alt={member.name} />
                  <div className="arts-multi-faculty-overlay">
                    <div className="arts-multi-faculty-experience">{member.experience}</div>
                  </div>
                </div>
                <div className="arts-multi-faculty-info">
                  <h3 className="arts-multi-faculty-name">{member.name}</h3>
                  <p className="arts-multi-faculty-position">{member.position}</p>
                  <p className="arts-multi-faculty-specialization">{member.specialization}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Specializations Section */}
      <section className="arts-multi-specializations">
        <div className="arts-multi-container">
          <div className="arts-multi-section-header">
            <h2 className="arts-multi-section-title">Creative Specializations</h2>
            <p className="arts-multi-section-subtitle">
              Explore diverse artistic and multimedia disciplines that match your creative passion
            </p>
          </div>
          <div className="arts-multi-specializations-grid">
            {specializations.map((spec, index) => {
              const IconComponent = spec.icon;
              return (
                <div key={index} className="arts-multi-specialization-card">
                  <div className="arts-multi-specialization-icon">
                    <IconComponent className="arts-multi-spec-icon" />
                  </div>
                  <h3 className="arts-multi-specialization-title">{spec.name}</h3>
                  <p className="arts-multi-specialization-description">{spec.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Facilities Section */}
      <section className="arts-multi-facilities">
        <div className="arts-multi-container">
          <div className="arts-multi-section-header">
            <h2 className="arts-multi-section-title">Creative Studios</h2>
            <p className="arts-multi-section-subtitle">
              Professional-grade studios and equipment for hands-on creative learning
            </p>
          </div>
          <div className="arts-multi-facilities-grid">
            {facilities.map((facility, index) => (
              <div key={index} className="arts-multi-facility-card">
                <div className="arts-multi-facility-icon-container">
                  {facility.icon}
                </div>
                <h3 className="arts-multi-facility-title">{facility.title}</h3>
                <p className="arts-multi-facility-description">{facility.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Research Section */}
      <section className="arts-multi-research">
        <div className="arts-multi-container">
          <div className="arts-multi-research-content">
            <div className="arts-multi-research-text">
              <h2 className="arts-multi-research-title">Creative Innovation</h2>
              <p className="arts-multi-research-description">
                Our department leads in creative research and innovation, 
                with ongoing projects in virtual reality art, interactive media, 
                digital storytelling, and emerging technologies in creative industries.
              </p>
              <div className="arts-multi-research-highlights">
                <div className="arts-multi-research-highlight">
                  <h4>Creative Projects</h4>
                  <p>20+ ongoing creative research projects in digital arts and multimedia</p>
                </div>
                <div className="arts-multi-research-highlight">
                  <h4>Exhibitions</h4>
                  <p>50+ student works exhibited in galleries and digital platforms annually</p>
                </div>
                <div className="arts-multi-research-highlight">
                  <h4>Industry Awards</h4>
                  <p>15+ awards won by students and faculty in creative competitions</p>
                </div>
              </div>
            </div>
            <div className="arts-multi-research-visual">
              <div className="arts-multi-research-animation">
                <div className="arts-multi-research-canvas arts-multi-research-canvas-1"></div>
                <div className="arts-multi-research-canvas arts-multi-research-canvas-2"></div>
                <div className="arts-multi-research-canvas arts-multi-research-canvas-3"></div>
                <div className="arts-multi-research-center">
                  <Palette className="arts-multi-research-icon" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="arts-multi-testimonials">
        <div className="arts-multi-container">
          <div className="arts-multi-section-header">
            <h2 className="arts-multi-section-title">Creative Success Stories</h2>
            <p className="arts-multi-section-subtitle">
              Hear from our graduates who are making their mark in the creative and multimedia industry
            </p>
          </div>
          <div className="arts-multi-testimonials-grid">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="arts-multi-testimonial-card">
                <div className="arts-multi-testimonial-content">
                  <p className="arts-multi-testimonial-text">"{testimonial.content}"</p>
                </div>
                <div className="arts-multi-testimonial-author">
                  <img src={testimonial.image} alt={testimonial.name} className="arts-multi-testimonial-avatar" />
                  <div className="arts-multi-testimonial-info">
                    <h4 className="arts-multi-testimonial-name">{testimonial.name}</h4>
                    <p className="arts-multi-testimonial-role">{testimonial.role}</p>
                    <p className="arts-multi-testimonial-year">{testimonial.year}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="arts-multi-cta">
        <div className="arts-multi-container">
          <div className="arts-multi-cta-content">
            <h2 className="arts-multi-cta-title">Transform Your Creative Vision</h2>
            <p className="arts-multi-cta-description">
              Join our arts and multimedia program and turn your creative passion 
              into a successful career in the digital creative industries.
            </p>
            <div className="arts-multi-cta-actions">
              <button className="arts-multi-btn arts-multi-btn-primary arts-multi-btn-large">Apply Now</button>
              <button className="arts-multi-btn arts-multi-btn-outline arts-multi-btn-large">Portfolio Review</button>
            </div>
          </div>
        </div>
      </section>
    </div>
    <Footer/>
    </>
  );
};

export default ArtsAndMultimedia;